import { attempt, isError, isNil } from 'lodash-es';
import { useEffect, useState } from 'react';

import { LS_KEY_AUTO_IGNORE_MODULE_LOAD_ERRORS } from 'consts';
import { useModuleLoader } from 'hooks/loader';
import services from 'services';
import { Json, ModuleConfig } from 'types';
import { isPluginModule } from 'utils';
import loadPlugins, { PluginModuleConfigWithKey } from 'utils/loadPlugins';
import { getAvailableModules } from 'utils/modules';

const isNonPreloginPluginModuleWithKey = (module: ModuleConfig): module is PluginModuleConfigWithKey =>
  isPluginModule(module) && !isNil(module.key) && !module.prelogin;

const usePlugins = () => {
  const moduleLoader = useModuleLoader();
  const [initialized, setInitialized] = useState(false);

  const { load: loadModule, getModulesMap } = moduleLoader;
  useEffect(() => {
    const availableModules = getAvailableModules();
    loadPlugins({
      pluginsToLoad: availableModules.filter(isNonPreloginPluginModuleWithKey),
      methods: ['preboot', 'boot'],
      loadModule,
      getModulesMap,
    }).then((pluginsLoadErrors) => {
      if (pluginsLoadErrors.length) {
        const autoIgnoreModuleLoadErrors = attempt(
          () => JSON.parse(localStorage.getItem(LS_KEY_AUTO_IGNORE_MODULE_LOAD_ERRORS) || '') || false,
        );
        if (isError(autoIgnoreModuleLoadErrors) || !autoIgnoreModuleLoadErrors) {
          services.appFrame.error.main.addError({
            title: 'errors.plugin_load_error.title',
            message: 'errors.plugin_load_error.message',
            details: {
              type: 'PLUGIN_LOAD_ERROR',
              timestamp: new Date(),
              errors: pluginsLoadErrors as Json,
            },
            actions: [
              {
                id: 'reload',
                icon: 'RefreshIcon',
                text: 'errors.plugin_load_error.actions.reload',
                onClick: () => window.location.reload(),
              },
              {
                id: 'ignore',
                icon: 'PlayCircleIcon',
                text: 'errors.plugin_load_error.actions.ignore',
                onClick: () => {
                  services.appFrame.error.main.closeError();
                  setInitialized(true);
                },
              },
            ],
          });
          return;
        }
      }
      setInitialized(true);
    });
  }, [loadModule, getModulesMap]); // NOTE: all these references must be static (constants)

  return initialized;
};

export default usePlugins;
